import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, formatPrice } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function PlanToggle({ m, plan, setPlan }) {
  const opt = (key, label) => (
    <button type="button" onClick={() => setPlan(key)} aria-pressed={plan === key} data-testid={`membership-plan-${key}`}
      className={`relative flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${plan === key ? "bg-bt-primary text-bt-bg" : "text-bt-heading hover:bg-bt-sage"}`}>
      {label}
      {key === "yearly" && m.yearly_badge && <span className="absolute -top-5 right-2 whitespace-nowrap rounded-full border border-bt-border bg-bt-sage px-2.5 py-0.5 text-[11px] font-semibold text-bt-primary" data-testid="membership-yearly-badge">{m.yearly_badge}</span>}
    </button>
  );
  return (
    <div className="mb-8 mt-5 flex rounded-full border border-bt-border bg-white p-1" role="group" data-testid="membership-plan-toggle">
      {opt("monthly", m.monthly_label)}
      {opt("yearly", m.yearly_label)}
    </div>
  );
}

export default function Membership() {
  const { settings } = useSite();
  const { user, isMember } = useAuth();
  const nav = useNavigate();
  const m = settings.membership;
  const [loading, setLoading] = useState(false);
  const yearlyOn = m.yearly_enabled && m.interval === "month";
  const [plan, setPlan] = useState(yearlyOn ? "yearly" : "monthly");
  const yearly = yearlyOn && plan === "yearly";
  const offer = !yearly && user?.winback_offer_until > new Date().toISOString();

  const join = async () => {
    if (!user) return nav("/register?next=/membership");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan: yearly ? "yearly" : "monthly" });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setLoading(false);
    }
  };

  return (
    <div className="container-bt py-14 sm:py-20" data-testid="membership-page">
      <div className="mx-auto max-w-3xl text-center">
        <p className="eyebrow mb-4">{m.name}</p>
        <h1 className="text-4xl leading-tight sm:text-5xl lg:text-6xl">{m.headline}</h1>
        <p className="mt-5 text-base md:text-lg">{m.subheadline}</p>
      </div>
      <div className="mx-auto mt-14 max-w-xl rounded-[36px] border border-bt-border bg-bt-surface p-8 shadow-[0_30px_60px_-40px_rgba(13,61,38,0.45)] sm:p-12" data-testid="membership-card">
        {m.offer && <p className="mb-5 inline-flex rounded-full bg-bt-sage px-4 py-1.5 text-sm font-semibold text-bt-primary" data-testid="membership-offer">{m.offer}</p>}
        {yearlyOn && <PlanToggle m={m} plan={plan} setPlan={setPlan} />}
        <p className="font-heading text-6xl text-bt-heading" data-testid="membership-price">{formatPrice(yearly ? { ...m, price: m.yearly_price } : m)}</p>
        <p className="mt-1 text-bt-sagedeep">per {yearly ? "year" : m.interval}{m.trial_text && ` · ${m.trial_text}`}</p>
        {offer && <p className="mt-4 rounded-2xl bg-bt-sage px-4 py-3 text-sm font-semibold text-bt-primary" data-testid="membership-winback-note">{m.winback_note}</p>}
        <ul className="my-9 space-y-4">
          {m.benefits.map((b, i) => (
            <li key={i} className="flex gap-3 text-base text-bt-heading" data-testid={`membership-benefit-${i}`}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bt-primary text-bt-bg"><Check size={14} /></span>{b}
            </li>
          ))}
        </ul>
        {isMember ? (
          <Link to="/account" className="btn-secondary w-full" data-testid="membership-already-member">You're a member. Go to your dashboard</Link>
        ) : (
          <button onClick={join} disabled={loading} className="btn-primary w-full text-lg" data-testid="membership-join-button">{loading ? "Opening checkout..." : m.cta}</button>
        )}
        <p className="mt-5 text-center text-sm text-bt-sagedeep">Not ready? <Link to="/free" className="underline">The Free Growth Hub</Link> is yours to keep.</p>
      </div>
      {settings.faqs?.length > 0 && (
        <div className="mx-auto mt-20 max-w-3xl">
          <h2 className="mb-6 text-3xl">Questions</h2>
          <Accordion type="single" collapsible>
            {settings.faqs.map((f, i) => (
              <AccordionItem key={i} value={`m${i}`} className="border-bt-border">
                <AccordionTrigger className="py-6 text-left font-heading text-lg text-bt-heading hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
